import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Bot, Fingerprint, UserCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/context/AuthContext";
import { BiometricGate } from "@/components/dashboard/BiometricGate";

export const ConnectionsSection = ({ t }) => {
  const [nearby, setNearby] = useState([]);
  const [conns, setConns] = useState([]);
  const [gate, setGate] = useState(null); // {type:'request'|'accept', id}

  const load = useCallback(async () => {
    const [n, c] = await Promise.all([api.get("/users/nearby"), api.get("/connections")]);
    setNearby(n.data);
    setConns(c.data);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const connIdsByUser = {};
  conns.forEach((c) => { if (c.status === "pending" || c.status === "connected") connIdsByUser[c.other.id] = c; });

  const doAction = async () => {
    try {
      if (gate.type === "request") {
        await api.post("/connections/request", { target_user_id: gate.id, biometric_ok: true });
      } else {
        await api.post(`/connections/${gate.id}/accept`, { biometric_ok: true });
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      throw err;
    }
  };

  const decline = async (id) => {
    await api.post(`/connections/${id}/decline`);
    await load();
  };

  const incoming = conns.filter((c) => c.status === "pending" && c.direction === "incoming");
  const connected = conns.filter((c) => c.status === "connected");

  return (
    <div data-testid="connections-section">
      <p className="text-sm text-[#A0A0B0] mb-8 flex items-center gap-2">
        <Fingerprint size={16} className="text-[#B026FF] shrink-0" /> {t.connSub}
      </p>

      {incoming.length > 0 && (
        <section className="mb-8">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#00FFAA] mb-3">{t.incomingTitle}</h2>
          <div className="space-y-3">
            {incoming.map((c) => (
              <div key={c.id} className="flex items-center justify-between bg-[#00FFAA]/5 border border-[#00FFAA]/25 rounded-xl px-5 py-4" data-testid="incoming-request-row">
                <div>
                  <p className="text-sm font-bold text-white">{c.other.avatar_name || c.other.name}</p>
                  <p className="text-[11px] text-white/40">@{c.other.username}</p>
                </div>
                <div className="flex gap-2">
                  <button data-testid="accept-connection-btn" onClick={() => setGate({ type: "accept", id: c.id })}
                    className="px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#00FFAA]/15 border border-[#00FFAA]/40 text-[#00FFAA] flex items-center gap-1.5">
                    <Fingerprint size={13} /> {t.acceptBtn}
                  </button>
                  <button data-testid="decline-connection-btn" onClick={() => decline(c.id)}
                    className="px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-white/5 border border-white/15 text-white/50">
                    {t.declineBtn}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mb-8">
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-3">{t.nearbyTitle}</h2>
        {nearby.length === 0 && <p className="text-xs text-white/30">{t.noNearby}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {nearby.map((u) => {
            const existing = connIdsByUser[u.id];
            const live = u.avatar_status === "Active_Proximity";
            return (
              <div key={u.id} className="bg-[#0F0F1A]/80 border border-white/10 rounded-2xl p-5" data-testid="nearby-avatar-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#B026FF]/30 to-[#00F5FF]/30 border border-white/10 flex items-center justify-center">
                    <Bot size={20} className="text-[#00F5FF]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate">{u.avatar_name || u.name}</p>
                    <p className="text-[11px] text-white/40 truncate">@{u.username}</p>
                  </div>
                  <span className={`ml-auto w-2 h-2 rounded-full shrink-0 ${live ? "bg-[#00FFAA] animate-pulse" : "bg-white/20"}`} />
                </div>
                {existing ? (
                  <span className={`inline-block text-[9px] font-bold tracking-widest px-3 py-1.5 rounded-full ${
                    existing.status === "connected" ? "bg-[#00FFAA]/10 text-[#00FFAA] border border-[#00FFAA]/40" : "bg-white/5 text-white/40 border border-white/10"
                  }`}>
                    {existing.status === "connected" ? t.connectedBadge : t.pendingBadge}
                  </span>
                ) : (
                  <button data-testid="connect-btn" onClick={() => setGate({ type: "request", id: u.id })}
                    className="w-full py-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#B026FF]/15 border border-[#B026FF]/40 text-[#D080FF] hover:bg-[#B026FF]/25 transition-colors flex items-center justify-center gap-1.5">
                    <Fingerprint size={13} /> {t.connectBtn}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {connected.length > 0 && (
        <section>
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-3">{t.myConnsTitle}</h2>
          <div className="space-y-3">
            {connected.map((c) => (
              <div key={c.id} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-4" data-testid="connected-row">
                <UserCheck size={18} className="text-[#00FFAA]" />
                <div>
                  <p className="text-sm font-bold text-white">{c.other.avatar_name || c.other.name}</p>
                  <p className="text-[11px] text-white/40">@{c.other.username}</p>
                </div>
                <span className="ml-auto text-[9px] font-bold tracking-widest px-3 py-1.5 rounded-full bg-[#00FFAA]/10 text-[#00FFAA] border border-[#00FFAA]/40">{t.connectedBadge}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <BiometricGate
        t={t}
        open={!!gate}
        title={t.connLockTitle}
        sub={t.connLockSub}
        successMsg={gate?.type === "accept" ? t.connAccepted : t.connSuccess}
        onClose={() => setGate(null)}
        onAuthenticated={doAction}
      />
    </div>
  );
};
